import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, Building2, Loader2, ShieldCheck, Calendar, Inbox } from "lucide-react";
import { api, downloadContadorUrl } from "@/lib/api";

export default function ContadorPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [erro, setErro] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .portalContador(token)
      .then(setData)
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100 text-zinc-400">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );

  if (erro || !data)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-100 px-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center text-red-500 mb-4">
          <ShieldCheck className="w-8 h-8" />
        </div>
        <h1 className="font-display text-2xl font-bold text-zinc-800">Link inválido</h1>
        <p className="text-zinc-500 mt-2">Este link não é válido ou foi revogado. Solicite um novo ao estabelecimento.</p>
      </div>
    );

  const { empresa, arquivos } = data;

  return (
    <div className="min-h-screen bg-zinc-100">
      <header className="bg-[#1c1c1f] grain">
        <div className="max-w-4xl mx-auto px-5 py-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/15 text-orange-400 text-xs font-semibold mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Portal do Contador
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center shrink-0 shadow-lg shadow-orange-500/30">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                {empresa.razao_social}
              </h1>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-zinc-400 font-mono">
                <span>CNPJ {empresa.cnpj}</span>
                <span>IE {empresa.ie}</span>
                <span>{empresa.municipio}/{empresa.uf}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-xl text-zinc-800">Arquivos SINTEGRA disponíveis</h2>
          <span className="text-sm text-zinc-400">{arquivos.length} arquivo(s)</span>
        </div>

        {arquivos.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-zinc-200 rounded-2xl py-20 text-center">
            <Inbox className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
            <p className="font-display font-semibold text-lg text-zinc-700">Nenhum arquivo publicado ainda</p>
            <p className="text-zinc-400 mt-1">Assim que o estabelecimento gerar, aparecerá aqui.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="contador-arquivos">
            {arquivos.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl p-4 md:p-5 border border-zinc-100 flex items-center gap-4 hover:shadow-lg hover:shadow-orange-500/5 transition"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-600 shrink-0">
                  <FileText className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm md:text-base text-zinc-800 font-medium truncate">{a.filename}</div>
                  <div className="text-xs text-zinc-400 flex flex-wrap gap-x-3 mt-1">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Competência {a.competencia}</span>
                    <span>versão {a.versao}</span>
                    <span>{a.num_registros} registros</span>
                    <span>{new Date(a.gerado_em).toLocaleString("pt-BR")}</span>
                  </div>
                </div>
                <a
                  href={downloadContadorUrl(token, a.id)}
                  data-testid={`contador-download-${a.id}`}
                  className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition shrink-0 shadow-md shadow-orange-500/20"
                >
                  <Download className="w-4 h-4" /> Baixar
                </a>
              </motion.div>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-zinc-400 mt-10">
          Gerador SINTEGRA · Convênio ICMS 57/95 · Minas Gerais
        </p>
      </main>
    </div>
  );
}
